/**
 * Conversational recurring job setup component for onboarding.
 * Guides users through setting up a recurring maintenance job step by step.
 */

import { faCalendar, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  nextFriday,
  nextMonday,
  nextSaturday,
  nextSunday,
  nextThursday,
  nextTuesday,
  nextWednesday,
  startOfMonth,
} from 'date-fns';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getSmartJobSuggestions } from '~/constants/onboardingPresets';

// =============================================================================
// TYPES
// =============================================================================

export type FrequencyType =
  | 'daily'
  | 'weekly'
  | 'fortnightly'
  | 'monthly'
  | 'yearly'
  | 'custom';

// For custom frequency, the base unit for repeat
export type CustomFrequencyUnit = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type RecurringJobConfig = {
  assetName: string; // Primary asset name (for single asset) or first asset (for multiple)
  assetNames?: string[]; // Multiple asset names when creating one job for multiple assets
  jobName: string;
  freq: FrequencyType;
  interval: string;
  byday?: string[];
  bymonthday?: string;
  startDate: string; // YYYY-MM-DD format
  count?: number; // Number of times to repeat (for fixed count jobs)
  isForever?: boolean; // If true, creates an automation instead of a job with fixed count
  selectedAssetIds?: string[]; // Specific asset IDs to include in the job (subset of all IDs for this asset type)
  // For custom frequency - store the underlying unit
  customUnit?: CustomFrequencyUnit;
};

// Asset info passed from parent to enable unit selection
export type AssetInfo = {
  name: string;
  type: string;
  quantity: number;
  spaces: (string | undefined)[];
  ids?: string[]; // Asset IDs after creation
};

type Step =
  | 'asset' // Multi-select assets (with expandable unit selection)
  | 'job'
  | 'frequency'
  | 'custom' // Custom frequency configuration (interval + unit)
  | 'days'
  | 'monthday' // Monthly day selection
  | 'start'
  | 'count'
  | 'done'
  | 'created';

// =============================================================================
// JOB SELECTION STEP
// =============================================================================

type JobSelectionProps = {
  assetNames: string[]; // Multiple asset names for this job
  assetTypes?: string[]; // Optional asset types for better job suggestions
  industry?: string; // Optional industry to filter out inappropriate jobs
  selectedJob: string | null;
  onSelect: (job: string) => void;
  onCustom: (name: string) => void;
  onInputFocus?: () => void;
};

function JobSelection({
  assetNames,
  assetTypes,
  industry,
  selectedJob,
  onSelect,
  onCustom,
  onInputFocus,
}: JobSelectionProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Use smart suggestions based on the selected assets, types, and industry
  const suggestions = getSmartJobSuggestions(assetNames, assetTypes, industry);

  // NOTE: This effect is necessary because the input isn't in the DOM when showCustom is false.
  // Once showCustom becomes true, we need to focus the newly rendered input.
  useEffect(() => {
    if (showCustom && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustom]);

  const handleCustomSubmit = () => {
    const trimmed = customName.trim();
    if (trimmed) {
      onCustom(trimmed);
      setShowCustom(false);
    }
  };

  // Descriptive asset label for UI
  const assetLabel =
    assetNames.length === 1
      ? assetNames[0]
      : `${assetNames.length} assets (${assetNames.join(', ')})`;

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>
        What recurring job would you like for{' '}
        <strong className='text-grey-70'>{assetLabel}</strong>?
      </div>

      <div className='flex flex-wrap gap-2'>
        {suggestions.map((job) => (
          <button
            key={job.name}
            type='button'
            onClick={() => onSelect(job.name)}
            className={classNames(
              'rounded-full border-2 px-4 py-2 text-sm font-medium transition-all',
              selectedJob === job.name
                ? 'border-brand bg-brand text-white'
                : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40 hover:shadow-sm'
            )}
          >
            {job.name}
          </button>
        ))}

        {showCustom ? (
          <div className='flex items-center gap-2'>
            <input
              ref={inputRef}
              type='text'
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCustomSubmit();
                if (e.key === 'Escape') setShowCustom(false);
              }}
              onFocus={onInputFocus}
              placeholder='Job name...'
              className='rounded-full border border-grey-20 px-4 py-2 text-sm focus:border-brand focus:outline-none'
            />
            <button
              type='button'
              onClick={handleCustomSubmit}
              disabled={!customName.trim()}
              className='rounded-full bg-brand px-3 py-2 text-sm text-white disabled:opacity-50'
            >
              <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={() => setShowCustom(true)}
            className='rounded-full border-2 border-dashed border-grey-30 px-4 py-2 text-sm font-medium text-grey-50 transition-all hover:border-brand hover:text-brand'
          >
            + Custom
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ASSET SELECTION STEP (Multi-select with expandable units)
// =============================================================================

// Asset with stable unique key
type AssetWithKey = AssetInfo & { key: string };

type AssetSelectionProps = {
  assets: AssetWithKey[];
  selectedAssets: string[]; // Asset keys that are selected for this job
  completedAssets: string[]; // Asset keys that already have jobs created
  onSelectionChange: (assetKeys: string[]) => void;
  // Unit selection per asset - managed by parent (keyed by asset key)
  selectedUnitsPerAsset: Map<string, number[]>;
  onUnitsChange: (assetKey: string, unitIndices: number[]) => void;
};

function AssetSelection({
  assets,
  selectedAssets,
  completedAssets,
  onSelectionChange,
  selectedUnitsPerAsset,
  onUnitsChange,
}: AssetSelectionProps) {
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null);
  const remainingAssets = assets.filter(
    (a) => !completedAssets.includes(a.key)
  );

  const handleToggleAsset = (assetKey: string) => {
    if (completedAssets.includes(assetKey)) return;

    if (selectedAssets.includes(assetKey)) {
      // Deselect
      onSelectionChange(selectedAssets.filter((a) => a !== assetKey));
      // Clear unit selection for this asset
      onUnitsChange(assetKey, []);
      // Clear expanded state if this asset was expanded
      if (expandedAsset === assetKey) {
        setExpandedAsset(null);
      }
    } else {
      // Select - initialize with all units
      onSelectionChange([...selectedAssets, assetKey]);
      const asset = assets.find((a) => a.key === assetKey);
      if (asset) {
        const allUnits = Array.from({ length: asset.quantity }, (_, i) => i);
        onUnitsChange(assetKey, allUnits);
      }
    }
  };

  const handleAssetKeyDown = (e: React.KeyboardEvent, assetKey: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleAsset(assetKey);
    }
  };

  const handleToggleUnit = (assetKey: string, unitIndex: number) => {
    const currentUnits = selectedUnitsPerAsset.get(assetKey) || [];
    if (currentUnits.includes(unitIndex)) {
      // Deselect - but ensure at least one remains
      const newSelection = currentUnits.filter((i) => i !== unitIndex);
      if (newSelection.length > 0) {
        onUnitsChange(assetKey, newSelection);
      }
    } else {
      // Select
      onUnitsChange(
        assetKey,
        [...currentUnits, unitIndex].sort((a, b) => a - b)
      );
    }
  };

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>
        Which assets would you like to include in this recurring job?
        <div className='mt-1 text-xs text-grey-50'>
          You can select multiple assets for one job (e.g., Inspect Pool Pump
          and Filter together)
        </div>
      </div>

      <div className='space-y-2'>
        {assets.map((asset) => {
          const isCompleted = completedAssets.includes(asset.key);
          const isSelected = selectedAssets.includes(asset.key);
          const isExpanded = expandedAsset === asset.key;
          const selectedUnits = selectedUnitsPerAsset.get(asset.key) || [];
          const hasMultipleUnits = asset.quantity > 1;

          return (
            <div key={asset.key} className='w-full'>
              {/* Asset Card */}
              <div
                role='button'
                tabIndex={isCompleted ? -1 : 0}
                onClick={() => !isCompleted && handleToggleAsset(asset.key)}
                onKeyDown={(e) =>
                  !isCompleted && handleAssetKeyDown(e, asset.key)
                }
                aria-disabled={isCompleted}
                aria-pressed={isSelected}
                className={classNames(
                  'flex w-full items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all',
                  isCompleted
                    ? 'cursor-not-allowed border-green-200 bg-green-50'
                    : isSelected
                    ? 'bg-brand/5 cursor-pointer border-brand'
                    : 'cursor-pointer border-grey-20 bg-white hover:border-grey-40'
                )}
              >
                <div className='flex items-center gap-3'>
                  {/* Checkbox */}
                  <div
                    className={classNames(
                      'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors',
                      isCompleted
                        ? 'border-green-500 bg-green-500 text-white'
                        : isSelected
                        ? 'border-brand bg-brand text-white'
                        : 'border-grey-30 bg-white'
                    )}
                  >
                    {(isSelected || isCompleted) && (
                      <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
                    )}
                  </div>

                  {/* Asset Info */}
                  <div>
                    <div
                      className={classNames(
                        'text-sm font-medium',
                        isCompleted
                          ? 'text-green-600'
                          : isSelected
                          ? 'text-brand'
                          : 'text-grey-70'
                      )}
                    >
                      {asset.name}
                      {hasMultipleUnits && ` (${asset.quantity} units)`}
                    </div>
                    {isSelected && selectedUnits.length < asset.quantity && (
                      <div className='text-xs text-grey-50'>
                        {selectedUnits.length} of {asset.quantity} units
                        selected
                      </div>
                    )}
                  </div>
                </div>

                {/* Expand/Collapse for multi-unit assets */}
                {isSelected && hasMultipleUnits && (
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedAsset(isExpanded ? null : asset.key);
                    }}
                    className='hover:bg-brand/10 ml-2 rounded px-2 py-1 text-xs text-brand'
                  >
                    {isExpanded ? 'Collapse' : 'Select units'}
                  </button>
                )}
              </div>

              {/* Expanded Unit Selection */}
              {isSelected && isExpanded && hasMultipleUnits && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className='overflow-hidden'
                >
                  <div className='ml-8 mt-2 space-y-2 rounded-lg border border-grey-20 bg-grey-5 p-3'>
                    <div className='text-grey-60 text-xs'>
                      Select which units to include:
                    </div>
                    {Array.from({ length: asset.quantity }).map((_, index) => {
                      const isUnitSelected = selectedUnits.includes(index);
                      const unitLabel =
                        asset.quantity > 1
                          ? `${asset.name} #${index + 1}`
                          : asset.name;
                      const spaceLabel = asset.spaces[index];

                      return (
                        <button
                          key={index}
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleUnit(asset.key, index);
                          }}
                          className={classNames(
                            'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs transition-all',
                            isUnitSelected
                              ? 'bg-brand/10 text-brand'
                              : 'bg-white text-grey-70 hover:bg-grey-10'
                          )}
                        >
                          <div
                            className={classNames(
                              'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                              isUnitSelected
                                ? 'border-brand bg-brand text-white'
                                : 'border-grey-30 bg-white'
                            )}
                          >
                            {isUnitSelected && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='h-2 w-2'
                              />
                            )}
                          </div>
                          <span>
                            {unitLabel}
                            {spaceLabel && (
                              <span className='ml-1 text-grey-50'>
                                → {spaceLabel}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {selectedAssets.length > 0 && (
        <div className='mt-3 text-sm text-brand'>
          ✓ {selectedAssets.length} asset
          {selectedAssets.length !== 1 ? 's' : ''} selected
        </div>
      )}

      {completedAssets.length > 0 && (
        <div className='mt-3 text-sm text-green-600'>
          ✓ {completedAssets.length} asset
          {completedAssets.length !== 1 ? 's' : ''} already have recurring jobs
        </div>
      )}

      {remainingAssets.length === 0 && completedAssets.length > 0 && (
        <div className='mt-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700'>
          All assets have recurring jobs configured!
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FREQUENCY SELECTION STEP
// =============================================================================

type FrequencySelectionProps = {
  selectedFreq: FrequencyType | null;
  onSelect: (freq: FrequencyType) => void;
};

function FrequencySelection({
  selectedFreq,
  onSelect,
}: FrequencySelectionProps) {
  const options: { value: FrequencyType; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'custom', label: 'Custom' },
  ];

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>
        How often should this job repeat?
      </div>

      {/* Horizontally scrollable container for frequency buttons */}
      <div className='-mx-1 overflow-x-auto px-1 pb-1'>
        <div className='flex gap-2'>
          {options.map((opt) => (
            <button
              key={opt.value}
              type='button'
              onClick={() => onSelect(opt.value)}
              className={classNames(
                'flex-shrink-0 rounded-full border-2 px-6 py-2 text-sm font-medium transition-all',
                selectedFreq === opt.value
                  ? 'border-brand bg-brand text-white'
                  : opt.value === 'custom'
                  ? 'border-dashed border-grey-30 bg-white text-grey-50 hover:border-brand hover:text-brand'
                  : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CUSTOM FREQUENCY SELECTION
// =============================================================================

type CustomFrequencySelectionProps = {
  interval: string;
  unit: CustomFrequencyUnit;
  selectedDays: string[]; // For weekly
  bymonthday: string | null; // For monthly
  onIntervalChange: (interval: string) => void;
  onUnitChange: (unit: CustomFrequencyUnit) => void;
  onDaysChange: (days: string[]) => void;
  onBymonthdayChange: (day: string) => void;
  onInputFocus?: () => void;
};

const CUSTOM_UNIT_OPTIONS: { value: CustomFrequencyUnit; label: string }[] = [
  { value: 'daily', label: 'Day(s)' },
  { value: 'weekly', label: 'Week(s)' },
  { value: 'monthly', label: 'Month(s)' },
  { value: 'yearly', label: 'Year(s)' },
];

function CustomFrequencySelection({
  interval,
  unit,
  selectedDays,
  bymonthday,
  onIntervalChange,
  onUnitChange,
  onDaysChange,
  onBymonthdayChange,
  onInputFocus,
}: CustomFrequencySelectionProps) {
  const toggleDay = (day: string) => {
    if (selectedDays.includes(day)) {
      onDaysChange(selectedDays.filter((d) => d !== day));
    } else {
      onDaysChange([...selectedDays, day]);
    }
  };

  return (
    <div className='w-full space-y-5'>
      <div className='text-grey-60 mb-4 text-sm'>
        Set your custom repeat schedule
      </div>

      {/* Interval and Unit Selection */}
      <div className='flex items-center gap-3'>
        <span className='text-grey-60 text-sm'>Every</span>
        <input
          type='number'
          min='1'
          max='999'
          value={interval}
          onChange={(e) => onIntervalChange(e.target.value)}
          onFocus={onInputFocus}
          className='w-20 rounded-lg border border-grey-20 px-3 py-2 text-center text-sm focus:border-brand focus:outline-none'
        />
        <div className='flex flex-wrap gap-2'>
          {CUSTOM_UNIT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type='button'
              onClick={() => onUnitChange(opt.value)}
              className={classNames(
                'rounded-full border-2 px-4 py-1.5 text-sm font-medium transition-all',
                unit === opt.value
                  ? 'border-brand bg-brand text-white'
                  : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly: Day selection */}
      {unit === 'weekly' && (
        <div className='space-y-3'>
          <div className='text-sm font-medium text-grey-70'>On which days?</div>
          <div className='flex flex-wrap gap-2'>
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type='button'
                onClick={() => toggleDay(day.value)}
                className={classNames(
                  'h-11 w-11 rounded-full border-2 text-sm font-medium transition-all',
                  selectedDays.includes(day.value)
                    ? 'border-brand bg-brand text-white'
                    : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
                )}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Monthly: Day of month selection */}
      {unit === 'monthly' && (
        <MonthDayCalendarPicker
          selectedDay={bymonthday}
          onDaySelect={onBymonthdayChange}
        />
      )}

      {/* Summary of selection */}
      <div className='text-grey-60 rounded-lg border border-grey-20 bg-grey-5 p-3 text-sm'>
        {getCustomFrequencySummary(interval, unit, selectedDays, bymonthday)}
      </div>
    </div>
  );
}

/**
 * Generate a human-readable summary of the custom frequency selection
 */
const getCustomFrequencySummary = (
  interval: string,
  unit: CustomFrequencyUnit,
  selectedDays: string[],
  bymonthday: string | null
): string => {
  const intervalNum = parseInt(interval, 10) || 1;
  const unitLabel =
    unit === 'daily'
      ? intervalNum === 1
        ? 'day'
        : 'days'
      : unit === 'weekly'
      ? intervalNum === 1
        ? 'week'
        : 'weeks'
      : unit === 'monthly'
      ? intervalNum === 1
        ? 'month'
        : 'months'
      : intervalNum === 1
      ? 'year'
      : 'years';

  let summary = `Every ${
    intervalNum === 1 ? '' : intervalNum + ' '
  }${unitLabel}`;

  if (unit === 'weekly' && selectedDays.length > 0) {
    const sortedDays = [...selectedDays].sort((a, b) => {
      const indexA = WEEKDAYS.findIndex((d) => d.value === a);
      const indexB = WEEKDAYS.findIndex((d) => d.value === b);
      // Defensive: if a day isn't found, maintain order (return 0)
      if (indexA === -1 || indexB === -1) return 0;
      return indexA - indexB;
    });
    summary += ` on ${sortedDays.join(', ')}`;
  }

  if (unit === 'monthly' && bymonthday) {
    summary += ` on day ${bymonthday}`;
  }

  return summary;
};

// =============================================================================
// WEEKLY DAYS SELECTION
// =============================================================================

type DaysSelectionProps = {
  selectedDays: string[];
  onDaysChange: (days: string[]) => void;
};

const WEEKDAYS = [
  { value: 'Monday', label: 'Mon' },
  { value: 'Tuesday', label: 'Tue' },
  { value: 'Wednesday', label: 'Wed' },
  { value: 'Thursday', label: 'Thu' },
  { value: 'Friday', label: 'Fri' },
  { value: 'Saturday', label: 'Sat' },
  { value: 'Sunday', label: 'Sun' },
];

function DaysSelection({ selectedDays, onDaysChange }: DaysSelectionProps) {
  const toggleDay = (day: string) => {
    if (selectedDays.includes(day)) {
      onDaysChange(selectedDays.filter((d) => d !== day));
    } else {
      onDaysChange([...selectedDays, day]);
    }
  };

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>On which days?</div>

      <div className='flex flex-wrap gap-2'>
        {WEEKDAYS.map((day) => (
          <button
            key={day.value}
            type='button'
            onClick={() => toggleDay(day.value)}
            className={classNames(
              'h-12 w-12 rounded-full border-2 text-sm font-medium transition-all',
              selectedDays.includes(day.value)
                ? 'border-brand bg-brand text-white'
                : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
            )}
          >
            {day.label}
          </button>
        ))}
      </div>

      {selectedDays.length > 0 && (
        <div className='text-grey-60 mt-3 text-sm'>
          Every{' '}
          {selectedDays
            .sort((a, b) => {
              const indexA = WEEKDAYS.findIndex((d) => d.value === a);
              const indexB = WEEKDAYS.findIndex((d) => d.value === b);
              // Defensive: if a day isn't found, maintain order (return 0)
              if (indexA === -1 || indexB === -1) return 0;
              return indexA - indexB;
            })
            .join(', ')}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MONTH DAY CALENDAR SELECTION (Airbnb-style)
// =============================================================================

type MonthDayCalendarPickerProps = {
  selectedDay: string | null;
  onDaySelect: (day: string) => void;
};

/**
 * Compact calendar picker for day of month (used in custom frequency selection)
 */
function MonthDayCalendarPicker({
  selectedDay,
  onDaySelect,
}: MonthDayCalendarPickerProps) {
  const today = new Date();
  const currentMonth = format(today, 'MMMM yyyy');
  const daysInMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  ).getDay();

  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className='space-y-3'>
      <div className='text-sm font-medium text-grey-70'>On which day?</div>
      <div className='mx-auto max-w-xs rounded-lg border border-grey-20 bg-white p-3'>
        <div className='mb-3 text-center text-sm font-semibold text-grey-90'>
          {currentMonth}
        </div>

        <div className='mb-2 grid grid-cols-7 gap-1'>
          {weekDays.map((day, index) => (
            <div
              key={`weekday-${index}`}
              className='flex h-8 items-center justify-center text-xs font-medium text-grey-50'
            >
              {day}
            </div>
          ))}
        </div>

        <div className='grid grid-cols-7 gap-1'>
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className='h-8' />;
            }

            const dayString = String(day);
            const isSelected = selectedDay === dayString;
            const isToday = day === today.getDate();

            return (
              <button
                key={day}
                type='button'
                onClick={() => onDaySelect(dayString)}
                className={classNames(
                  'flex h-8 w-full items-center justify-center rounded-lg text-sm font-medium transition-all',
                  isSelected
                    ? 'bg-brand text-white shadow-sm'
                    : isToday
                    ? 'border border-brand text-brand hover:bg-shade-10'
                    : 'text-grey-70 hover:bg-grey-10'
                )}
              >
                {day}
              </button>
            );
          })}
        </div>

        {selectedDay && (
          <div className='text-grey-60 mt-3 text-center text-xs'>
            Day <strong>{selectedDay}</strong> of every month
          </div>
        )}
      </div>
    </div>
  );
}

type MonthDayCalendarSelectionProps = {
  selectedDay: string | null;
  onDaySelect: (day: string) => void;
  onComplete: () => void;
};

function MonthDayCalendarSelection({
  selectedDay,
  onDaySelect,
  onComplete,
}: MonthDayCalendarSelectionProps) {
  const today = new Date();
  const currentMonth = format(today, 'MMMM yyyy');
  const daysInMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  ).getDate();

  // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  ).getDay();

  // Create calendar grid
  const calendarDays: (number | null)[] = [];

  // Add empty cells for days before the first of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className='w-full space-y-4'>
      <div className='text-grey-60 mb-4 text-sm'>
        On which day of the month?
      </div>

      {/* Calendar Container */}
      <div className='mx-auto max-w-md rounded-lg border border-grey-20 bg-white p-4'>
        {/* Month Header */}
        <div className='mb-4 text-center text-base font-semibold text-grey-90'>
          {currentMonth}
        </div>

        {/* Weekday Headers */}
        <div className='mb-2 grid grid-cols-7 gap-1'>
          {weekDays.map((day, index) => (
            <div
              key={`weekday-${index}`}
              className='flex h-10 items-center justify-center text-xs font-medium text-grey-50'
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className='grid grid-cols-7 gap-1'>
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className='h-10' />;
            }

            const dayString = String(day);
            const isSelected = selectedDay === dayString;
            const isToday = day === today.getDate();

            return (
              <button
                key={day}
                type='button'
                onClick={() => onDaySelect(dayString)}
                className={classNames(
                  'flex h-10 w-full items-center justify-center rounded-lg text-sm font-medium transition-all',
                  isSelected
                    ? 'bg-brand text-white shadow-sm'
                    : isToday
                    ? 'border-2 border-brand text-brand hover:bg-shade-10'
                    : 'text-grey-70 hover:bg-grey-10'
                )}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Helper text */}
        {selectedDay && (
          <div className='text-grey-60 mt-4 text-center text-sm'>
            Selected: Day <strong>{selectedDay}</strong> of every month
          </div>
        )}
      </div>

      <button
        type='button'
        onClick={onComplete}
        disabled={!selectedDay}
        className='w-full rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-opacity hover:bg-brand-dark disabled:opacity-50'
      >
        Continue
      </button>
    </div>
  );
}

// =============================================================================
// START DATE SELECTION
// =============================================================================

type StartDateSelectionProps = {
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  freq: FrequencyType | null;
  byday: string[];
  bymonthday: string | null;
  customUnit: CustomFrequencyUnit | null;
};

// =============================================================================
// START DATE SELECTION
// =============================================================================

/**
 * Get the next occurrence of a specific weekday
 */
const getNextWeekday = (today: Date, weekday: string): Date => {
  const weekdayMap: Record<string, (date: Date) => Date> = {
    Monday: nextMonday,
    Tuesday: nextTuesday,
    Wednesday: nextWednesday,
    Thursday: nextThursday,
    Friday: nextFriday,
    Saturday: nextSaturday,
    Sunday: nextSunday,
  };

  const func = weekdayMap[weekday];
  return func ? func(today) : addDays(today, 7); // fallback to next week
};

/**
 * Get the next occurrence of a specific day of the month
 * First tries the current month, only falls back to next month if current month candidate is before today.
 * Handles month length edge-cases by clamping to the last day of the month.
 */
const getNextMonthDay = (today: Date, dayOfMonth: number): Date => {
  // Normalize today to start of day for comparison
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  // Build candidate date in current month
  const startOfCurrentMonth = startOfMonth(today);
  const lastDayOfCurrentMonth = endOfMonth(startOfCurrentMonth).getDate();
  // Clamp dayOfMonth to the last day of the current month
  const clampedDay = Math.min(dayOfMonth, lastDayOfCurrentMonth);
  const currentMonthCandidate = new Date(
    startOfCurrentMonth.getFullYear(),
    startOfCurrentMonth.getMonth(),
    clampedDay
  );

  // If the current month candidate is today or in the future, return it
  if (currentMonthCandidate >= todayStart) {
    return currentMonthCandidate;
  }

  // Otherwise, compute and return the same calculation for next month
  const nextMonth = addMonths(today, 1);
  const startOfNextMonth = startOfMonth(nextMonth);
  const lastDayOfNextMonth = endOfMonth(startOfNextMonth).getDate();
  // Clamp dayOfMonth to the last day of the next month
  const clampedDayNext = Math.min(dayOfMonth, lastDayOfNextMonth);
  return new Date(
    startOfNextMonth.getFullYear(),
    startOfNextMonth.getMonth(),
    clampedDayNext
  );
};

/**
 * Generate smart start date options based on frequency configuration
 */
const getSmartStartOptions = (
  freq: FrequencyType | null,
  byday: string[],
  bymonthday: string | null,
  customUnit: CustomFrequencyUnit | null,
  today: Date
) => {
  const options: { label: string; date: string }[] = [];

  // Base options that are always available
  options.push({
    label: 'Next Week',
    date: format(addDays(today, 7), 'yyyy-MM-dd'),
  });

  options.push({
    label: 'Next Month',
    date: format(addMonths(today, 1), 'yyyy-MM-dd'),
  });

  // Smart options based on frequency
  if (freq === 'weekly' && byday.length > 0) {
    // For weekly jobs, offer to start on the next occurrence of selected days
    byday.forEach((day) => {
      const nextDate = getNextWeekday(today, day);
      options.unshift({
        label: `Next ${day}`,
        date: format(nextDate, 'yyyy-MM-dd'),
      });
    });
  } else if (freq === 'fortnightly' && byday.length > 0) {
    // For fortnightly jobs, same as weekly
    byday.forEach((day) => {
      const nextDate = getNextWeekday(today, day);
      options.unshift({
        label: `Next ${day}`,
        date: format(nextDate, 'yyyy-MM-dd'),
      });
    });
  } else if (freq === 'monthly' && bymonthday) {
    // For monthly jobs, offer to start on the specified day of next month
    const dayNum = parseInt(bymonthday, 10);
    if (dayNum && dayNum >= 1 && dayNum <= 31) {
      const nextDate = getNextMonthDay(today, dayNum);
      options.unshift({
        label: `Day ${dayNum} of next month`,
        date: format(nextDate, 'yyyy-MM-dd'),
      });
    }
  } else if (freq === 'custom') {
    // For custom frequency, adapt based on custom unit
    if (customUnit === 'weekly' && byday.length > 0) {
      byday.forEach((day) => {
        const nextDate = getNextWeekday(today, day);
        options.unshift({
          label: `Next ${day}`,
          date: format(nextDate, 'yyyy-MM-dd'),
        });
      });
    } else if (customUnit === 'monthly' && bymonthday) {
      const dayNum = parseInt(bymonthday, 10);
      if (dayNum && dayNum >= 1 && dayNum <= 31) {
        const nextDate = getNextMonthDay(today, dayNum);
        options.unshift({
          label: `Day ${dayNum} of next month`,
          date: format(nextDate, 'yyyy-MM-dd'),
        });
      }
    } else {
      // For daily, yearly, or other custom units, add a generic "Next Monday" option
      options.unshift({
        label: 'Next Monday',
        date: format(nextMonday(today), 'yyyy-MM-dd'),
      });
    }
  } else {
    // For daily, yearly, or other cases, add a generic "Next Monday" option
    options.unshift({
      label: 'Next Monday',
      date: format(nextMonday(today), 'yyyy-MM-dd'),
    });
  }

  // Remove duplicates (in case we added the same date multiple times)
  const uniqueOptions = options.filter(
    (option, index, self) =>
      index === self.findIndex((o) => o.date === option.date)
  );

  return uniqueOptions;
};

function StartDateSelection({
  selectedDate,
  onDateSelect,
  freq,
  byday,
  bymonthday,
  customUnit,
}: StartDateSelectionProps) {
  const [showCustomDate, setShowCustomDate] = useState(false);
  const today = new Date();

  const quickOptions = getSmartStartOptions(
    freq,
    byday,
    bymonthday,
    customUnit,
    today
  );

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>
        When should we start scheduling this?
      </div>

      <div className='flex flex-wrap gap-2'>
        {quickOptions.map((opt) => (
          <button
            key={opt.label}
            type='button'
            onClick={() => onDateSelect(opt.date)}
            className={classNames(
              'rounded-full border-2 px-4 py-2 text-sm font-medium transition-all',
              selectedDate === opt.date
                ? 'border-brand bg-brand text-white'
                : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
            )}
          >
            {opt.label}
          </button>
        ))}

        {showCustomDate ? (
          <div className='flex items-center gap-2'>
            <input
              type='date'
              min={format(addDays(today, 1), 'yyyy-MM-dd')}
              onChange={(e) => {
                if (e.target.value) {
                  onDateSelect(e.target.value);
                  setShowCustomDate(false);
                }
              }}
              className='rounded-full border border-grey-20 px-4 py-2 text-sm focus:border-brand focus:outline-none'
            />
          </div>
        ) : (
          <button
            type='button'
            onClick={() => setShowCustomDate(true)}
            className='flex items-center gap-2 rounded-full border-2 border-dashed border-grey-30 px-4 py-2 text-sm font-medium text-grey-50 transition-all hover:border-brand hover:text-brand'
          >
            <FontAwesomeIcon icon={faCalendar} className='h-3 w-3' />
            Pick a date
          </button>
        )}
      </div>

      {selectedDate && (
        <div className='text-grey-60 mt-3 text-sm'>
          Starting{' '}
          <strong>
            {format(new Date(selectedDate + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
          </strong>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// REPEAT COUNT SELECTION
// =============================================================================

type RepeatCountSelectionProps = {
  selectedCount: number | null;
  isForever: boolean;
  onCountSelect: (count: number | null, isForever: boolean) => void;
  onInputFocus?: () => void;
};

// Repeat count options (number of jobs to create)
const REPEAT_OPTIONS: { label: string; value: number }[] = [
  { label: '1 time', value: 1 },
  { label: '2 times', value: 2 },
  { label: '3 times', value: 3 },
];

function RepeatCountSelection({
  selectedCount,
  isForever,
  onCountSelect,
  onInputFocus,
}: RepeatCountSelectionProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handleCustomSubmit = () => {
    const num = parseInt(customValue, 10);
    if (!isNaN(num) && num > 0) {
      onCountSelect(num, false);
      setShowCustom(false);
    }
  };

  const handleForeverSelect = () => {
    onCountSelect(null, true);
  };

  return (
    <div className='w-full'>
      <div className='text-grey-60 mb-4 text-sm'>
        How long should this job repeat?
      </div>

      <div className='flex flex-wrap gap-2'>
        {/* Forever option - creates automation */}
        <button
          type='button'
          onClick={handleForeverSelect}
          className={classNames(
            'rounded-full border-2 px-4 py-2 text-sm font-medium transition-all',
            isForever
              ? 'border-brand bg-brand text-white'
              : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
          )}
        >
          Ongoing
        </button>

        {REPEAT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type='button'
            onClick={() => onCountSelect(opt.value, false)}
            className={classNames(
              'rounded-full border-2 px-4 py-2 text-sm font-medium transition-all',
              !isForever && selectedCount === opt.value
                ? 'border-brand bg-brand text-white'
                : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
            )}
          >
            {opt.label}
          </button>
        ))}

        {showCustom ? (
          <div className='flex items-center gap-2'>
            <input
              type='number'
              min='1'
              max='999'
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCustomSubmit();
                if (e.key === 'Escape') setShowCustom(false);
              }}
              onFocus={onInputFocus}
              placeholder='jobs'
              className='w-24 rounded-full border border-grey-20 px-4 py-2 text-center text-sm focus:border-brand focus:outline-none'
              autoFocus
            />
            <button
              type='button'
              onClick={handleCustomSubmit}
              disabled={
                !customValue ||
                isNaN(parseInt(customValue, 10)) ||
                parseInt(customValue, 10) <= 0
              }
              className='rounded-full bg-brand px-3 py-2 text-sm text-white disabled:opacity-50'
            >
              <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={() => setShowCustom(true)}
            className='rounded-full border-2 border-dashed border-grey-30 px-4 py-2 text-sm font-medium text-grey-50 transition-all hover:border-brand hover:text-brand'
          >
            Custom
          </button>
        )}
      </div>

      {isForever && (
        <div className='text-grey-60 mt-3 text-sm'>
          This job will continue ongoing. You can adjust the schedule or stop it
          in Settings → Automations.
        </div>
      )}
      {!isForever && selectedCount !== null && (
        <div className='text-grey-60 mt-3 text-sm'>
          This will create {selectedCount}{' '}
          {selectedCount === 1 ? 'job' : 'jobs'}.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SUMMARY
// =============================================================================

type SummaryProps = {
  config: RecurringJobConfig;
  assetInfos?: AssetWithKey[]; // All assets involved in this job (with keys for lookups)
  selectedUnitsPerAsset?: Map<string, number[]>; // Unit selection per asset (keyed by asset key)
};

function Summary({ config, assetInfos, selectedUnitsPerAsset }: SummaryProps) {
  const getFreqLabel = (): string => {
    const intervalNum = parseInt(config.interval, 10) || 1;

    switch (config.freq) {
      case 'daily':
        return intervalNum === 1 ? 'Every day' : `Every ${intervalNum} days`;
      case 'weekly':
        return config.byday?.length
          ? `Every ${config.byday.join(', ')}`
          : 'Every week';
      case 'fortnightly':
        return 'Every 2 weeks';
      case 'monthly':
        return config.bymonthday
          ? `Every month on day ${config.bymonthday}`
          : 'Every month';
      case 'yearly':
        return 'Every year';
      case 'custom': {
        // For custom, use the underlying customUnit
        const unit = config.customUnit || 'daily';
        const unitLabel =
          unit === 'daily'
            ? intervalNum === 1
              ? 'day'
              : 'days'
            : unit === 'weekly'
            ? intervalNum === 1
              ? 'week'
              : 'weeks'
            : unit === 'monthly'
            ? intervalNum === 1
              ? 'month'
              : 'months'
            : intervalNum === 1
            ? 'year'
            : 'years';

        let summary = `Every ${
          intervalNum === 1 ? '' : intervalNum + ' '
        }${unitLabel}`;

        if (unit === 'weekly' && config.byday?.length) {
          summary += ` on ${config.byday.join(', ')}`;
        }
        if (unit === 'monthly' && config.bymonthday) {
          summary += ` on day ${config.bymonthday}`;
        }
        return summary;
      }
      default:
        return 'Custom schedule';
    }
  };

  const freqLabel = getFreqLabel();

  const getRepeatLabel = () => {
    if (config.isForever) return 'Ongoing';
    if (!config.count) return 'Not set';
    return `${config.count} ${config.count === 1 ? 'job' : 'jobs'}`;
  };

  // Build asset label showing all selected assets and their units
  // NOTE: Uses asset keys (not names) for selectedUnitsPerAsset lookups to avoid collisions
  // when multiple assets share the same name. Asset keys are stable and unique.
  const getAssetLabel = () => {
    if (!assetInfos || assetInfos.length === 0) {
      return config.assetNames?.join(', ') || config.assetName;
    }

    if (assetInfos.length === 1 && assetInfos[0]) {
      const asset = assetInfos[0];
      // Use asset.key for lookup (not asset.name) to avoid collisions
      const selectedUnits = selectedUnitsPerAsset?.get(asset.key) || [];

      // Show unit count if partial selection
      if (
        asset.quantity > 1 &&
        selectedUnits.length > 0 &&
        selectedUnits.length < asset.quantity
      ) {
        return `${asset.name} (${selectedUnits.length} of ${asset.quantity} units)`;
      }

      return asset.name;
    }

    // Multiple assets
    const assetLabels = assetInfos.map((asset) => {
      // Use asset.key for lookup (not asset.name) to avoid collisions
      const selectedUnits = selectedUnitsPerAsset?.get(asset.key) || [];
      if (
        asset.quantity > 1 &&
        selectedUnits.length > 0 &&
        selectedUnits.length < asset.quantity
      ) {
        return `${asset.name} (${selectedUnits.length}/${asset.quantity})`;
      }
      return asset.name;
    });

    return assetLabels.join(', ');
  };

  const assetLabel = getAssetLabel();

  return (
    <div className='border-brand/20 bg-brand/5 w-full rounded-lg border p-4'>
      <div className='mb-2 text-sm font-medium text-brand'>Job Summary</div>
      <div className='space-y-1 text-sm text-grey-70'>
        <div>
          <strong>
            {assetInfos && assetInfos.length > 1 ? 'Assets' : 'Asset'}:
          </strong>{' '}
          {assetLabel}
        </div>
        <div>
          <strong>Job:</strong> {config.jobName}
        </div>
        <div>
          <strong>Frequency:</strong> {freqLabel}
        </div>
        <div>
          <strong>Starting:</strong>{' '}
          {format(new Date(config.startDate + 'T00:00:00'), 'MMMM d, yyyy')}
        </div>
        <div>
          <strong>Duration:</strong> {getRepeatLabel()}
        </div>
      </div>
      {config.isForever && (
        <div className='mt-3 rounded bg-shade-10 p-2 text-xs text-brand'>
          💡 This job will repeat forever. You can adjust the schedule or stop
          it in Settings → Automations.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export type RecurringJobSetupProps = {
  industry: string; // Kept for potential future use (industry-specific customization)
  /** Asset details including IDs, quantities, and space assignments */
  assets: AssetInfo[];
  /** @deprecated Use `assets` instead. Kept for backwards compatibility */
  assetNames?: string[];
  onComplete: (config: RecurringJobConfig) => void;
  onSkip: () => void;
  onBack?: () => void;
  /** Already completed job configs (for tracking which assets have jobs) */
  completedConfigs?: RecurringJobConfig[];
  /** Handler for finishing onboarding from this component */
  onFinish?: () => void;
  /** Handler for text input focus - triggers scroll on mobile to keep input visible */
  onInputFocus?: () => void;
  /** Loading state for the create button */
  isSubmitting?: boolean;
};

export function RecurringJobSetup({
  industry: _industry,
  assets,
  assetNames: legacyAssetNames,
  onComplete,
  onSkip,
  onBack,
  completedConfigs = [],
  onFinish,
  onInputFocus,
  isSubmitting = false,
}: RecurringJobSetupProps) {
  // Support legacy assetNames prop - convert to AssetInfo format if assets not provided
  const assetList: AssetInfo[] = useMemo(
    () =>
      assets ??
      (legacyAssetNames?.map((name) => ({
        name,
        type: '',
        quantity: 1,
        spaces: [undefined],
        ids: undefined,
      })) ||
        []),
    [assets, legacyAssetNames]
  );

  // Generate stable unique keys for each asset (use first ID if available, otherwise use name::index)
  const assetsWithKeys: AssetWithKey[] = useMemo(
    () =>
      assetList.map((asset, index) => ({
        ...asset,
        key:
          asset.ids && asset.ids.length > 0
            ? asset.ids[0]
            : `${asset.name}::${index}`,
      })),
    [assetList]
  );

  // Create a map from key to asset for quick lookups
  const assetKeyToAsset = useMemo(
    () => new Map(assetsWithKeys.map((a) => [a.key, a])),
    [assetsWithKeys]
  );

  // Create a map from name to keys (for completed assets lookup)
  const assetNameToKeys = useMemo(() => {
    const map = new Map<string, string[]>();
    assetsWithKeys.forEach((asset) => {
      const existing = map.get(asset.name) || [];
      map.set(asset.name, [...existing, asset.key]);
    });
    return map;
  }, [assetsWithKeys]);

  const assetNames = assetList.map((a) => a.name);
  const hasMultipleAssets = assetList.length > 1;
  // Extract all asset keys from completed configs - handle both single (assetName) and multi-asset (assetNames) jobs
  const completedAssetKeys = useMemo(
    () =>
      completedConfigs.flatMap((c) => {
        const names = c.assetNames || [c.assetName];
        return names.flatMap((name) => assetNameToKeys.get(name) || []);
      }),
    [completedConfigs, assetNameToKeys]
  );
  const remainingAssets = assetNames.filter((a) => {
    const keys = assetNameToKeys.get(a) || [];
    return !keys.every((key) => completedAssetKeys.includes(key));
  });

  // Always start at asset selection (multi-select)
  const [step, setStep] = useState<Step>('asset');

  // Multi-select: user can select multiple assets for one job (using keys)
  const [selectedAssetKeys, setSelectedAssetKeys] = useState<string[]>([]);

  // Track which unit indices are selected for each asset (Map<assetKey, unitIndices[]>)
  const [selectedUnitsPerAsset, setSelectedUnitsPerAsset] = useState<
    Map<string, number[]>
  >(new Map());

  // NOTE: Reset step and selection when the asset list changes.
  // This handles the case where the component receives a different set of assets
  // (e.g., user goes back and adds/removes assets). Without this, the component
  // would keep its stale state from the initial mount.
  const prevAssetKeysRef = useRef<string[]>(assetsWithKeys.map((a) => a.key));
  useEffect(() => {
    const prevKeys = prevAssetKeysRef.current;
    const currentKeys = assetsWithKeys.map((a) => a.key);

    // Check if asset list has changed (different length or different keys)
    const hasChanged =
      prevKeys.length !== currentKeys.length ||
      prevKeys.some((key, i) => key !== currentKeys[i]);

    if (hasChanged) {
      // Reset to asset selection
      setStep('asset');
      setSelectedAssetKeys([]);
      setSelectedUnitsPerAsset(new Map());
      prevAssetKeysRef.current = currentKeys;
    }
  }, [assetsWithKeys]);

  // Track if we've already auto-finished to prevent multiple calls
  const hasAutoFinishedRef = useRef(false);

  // Auto-finish when all assets have recurring jobs configured
  useEffect(() => {
    // Only auto-finish if:
    // 1. All assets have jobs (remainingAssets.length === 0)
    // 2. We're in the 'created' step (job was just created)
    // 3. We have completed configs (to avoid triggering on initial mount)
    // 4. We haven't already auto-finished
    if (
      remainingAssets.length === 0 &&
      step === 'created' &&
      completedConfigs.length > 0 &&
      !hasAutoFinishedRef.current &&
      onFinish
    ) {
      hasAutoFinishedRef.current = true;
      // Small delay to allow the success message to be visible
      const timeoutId = setTimeout(() => {
        onFinish();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [remainingAssets.length, step, completedConfigs.length, onFinish]);

  // Reset auto-finish flag when assets change or when step changes away from 'created'
  useEffect(() => {
    if (step !== 'created') {
      hasAutoFinishedRef.current = false;
    }
  }, [step]);

  const [jobName, setJobName] = useState<string | null>(null);
  const [freq, setFreq] = useState<FrequencyType | null>(null);
  const [interval, setInterval] = useState<string>('1');
  const [customUnit, setCustomUnit] = useState<CustomFrequencyUnit>('weekly');
  const [byday, setByday] = useState<string[]>([]);
  const [bymonthday, setBymonthday] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [isForever, setIsForever] = useState(false);

  // Track when a job was just created (for showing post-creation UI)
  const [lastCreatedAsset, setLastCreatedAsset] = useState<string | null>(null);

  /**
   * Get the previous step for proper back navigation within the job creation flow.
   * This ensures the back button navigates step-by-step instead of jumping to the start.
   */
  const getPreviousStep = (currentStep: Step): Step | null => {
    switch (currentStep) {
      case 'asset':
        return null; // Exit to parent (onBack)
      case 'job':
        return 'asset'; // Always go back to asset selection
      case 'frequency':
        return 'job';
      case 'custom':
        return 'frequency';
      case 'days':
        return 'frequency';
      case 'monthday':
        return 'frequency';
      case 'start':
        // Navigate back based on which frequency was selected
        if (freq === 'custom') return 'custom';
        if (freq === 'weekly' || freq === 'fortnightly') return 'days';
        if (freq === 'monthly') return 'monthday';
        return 'frequency'; // daily/yearly go straight from frequency to start
      case 'count':
        return 'start';
      case 'done':
        return 'count';
      case 'created':
        return null; // No going back from created state
      default:
        return null;
    }
  };

  /**
   * Handle back navigation within the job creation flow.
   * If there's a previous internal step, navigate to it; otherwise call onBack to exit.
   */
  const handleGoBack = () => {
    const previousStep = getPreviousStep(step);
    if (previousStep) {
      setStep(previousStep);
    } else if (onBack) {
      onBack();
    }
  };

  // Track previous completedConfigs length to detect new job creation
  const prevCompletedCountRef = useRef(completedConfigs.length);

  // NOTE: This effect detects when a new job was successfully created (completedConfigs grew)
  // and transitions the UI to the 'created' state to show next-step options.
  useEffect(() => {
    const prevCount = prevCompletedCountRef.current;
    const currentCount = completedConfigs.length;

    if (currentCount > prevCount && step === 'done') {
      // A new job was just created - transition to 'created' state
      const newestConfig = completedConfigs[completedConfigs.length - 1];
      setLastCreatedAsset(newestConfig?.assetName || null);
      setStep('created');
    }

    prevCompletedCountRef.current = currentCount;
  }, [completedConfigs.length, step, completedConfigs]);

  const handleAssetSelectionConfirm = () => {
    if (selectedAssetKeys.length === 0) return;
    setStep('job');
  };

  const handleUnitsChange = (assetKey: string, unitIndices: number[]) => {
    setSelectedUnitsPerAsset((prev) => {
      const newMap = new Map(prev);
      newMap.set(assetKey, unitIndices);
      return newMap;
    });
  };

  const handleJobSelect = (name: string) => {
    setJobName(name);
    // Don't auto-select frequency - user should explicitly choose
    setStep('frequency');
  };

  const handleFreqSelect = (f: FrequencyType) => {
    setFreq(f);

    switch (f) {
      case 'daily':
        setInterval('1');
        setStep('start');
        break;
      case 'weekly':
        setInterval('1');
        setStep('days');
        break;
      case 'fortnightly':
        // Fortnightly is weekly with interval 2
        setInterval('2');
        setStep('days');
        break;
      case 'monthly':
        setInterval('1');
        setStep('monthday');
        break;
      case 'yearly':
        setInterval('1');
        setStep('start');
        break;
      case 'custom':
        // Go to custom frequency configuration
        setInterval('1');
        setCustomUnit('weekly');
        setStep('custom');
        break;
    }
  };

  const handleDaysComplete = () => {
    if (byday.length > 0) {
      setStep('start');
    }
  };

  const handleMonthdayComplete = () => {
    if (bymonthday) {
      setStep('start');
    }
  };

  const handleCustomFrequencyComplete = () => {
    // Validate based on the selected unit
    if (customUnit === 'weekly' && byday.length === 0) {
      return; // Need at least one day selected for weekly
    }
    if (customUnit === 'monthly' && !bymonthday) {
      return; // Need a day selected for monthly
    }
    setStep('start');
  };

  const handleStartDateSelect = (date: string) => {
    setStartDate(date);
    setStep('count');
  };

  const handleCountSelect = (c: number | null, forever: boolean) => {
    setCount(c);
    setIsForever(forever);
    setStep('done');
  };

  const handleComplete = () => {
    if (selectedAssetKeys.length === 0 || !jobName || !freq || !startDate)
      return;

    // Convert keys to names for the config
    const selectedAssetNames = selectedAssetKeys
      .map((key) => assetKeyToAsset.get(key)?.name)
      .filter((name): name is string => !!name);

    // Guard: Ensure we have at least one asset name before accessing selectedAssetNames[0]
    if (selectedAssetNames.length === 0) {
      toast.error(
        'Please select at least one asset to create a recurring job.'
      );
      return;
    }

    // Collect all selected asset IDs from all selected assets
    const allSelectedAssetIds: string[] = [];

    selectedAssetKeys.forEach((assetKey) => {
      const asset = assetKeyToAsset.get(assetKey);
      if (!asset?.ids || asset.ids.length === 0) return;

      const selectedUnits = selectedUnitsPerAsset.get(assetKey) || [];
      // Get IDs for the selected units
      const assetIds = selectedUnits
        .filter((idx) => idx < asset.ids!.length)
        .map((idx) => asset.ids![idx]);

      allSelectedAssetIds.push(...assetIds);
    });

    // Guard: Prevent submitting a job with no assets - show user-visible error
    if (allSelectedAssetIds.length === 0) {
      toast.error(
        'No valid asset IDs found. Please ensure selected assets have been created and try again.'
      );
      return;
    }

    // Determine the final interval and byday/bymonthday based on frequency type
    let finalInterval = interval;
    let finalByday: string[] | undefined;
    let finalBymonthday: string | undefined;
    let finalCustomUnit: CustomFrequencyUnit | undefined;

    switch (freq) {
      case 'daily':
      case 'yearly':
        // Simple frequencies - no extra config needed
        finalInterval = '1';
        break;
      case 'weekly':
        finalInterval = '1';
        finalByday = byday.length > 0 ? byday : undefined;
        break;
      case 'fortnightly':
        finalInterval = '2';
        finalByday = byday.length > 0 ? byday : undefined;
        break;
      case 'monthly':
        finalInterval = '1';
        finalBymonthday = bymonthday ?? undefined;
        break;
      case 'custom':
        finalInterval = interval;
        finalCustomUnit = customUnit;
        if (customUnit === 'weekly') {
          finalByday = byday.length > 0 ? byday : undefined;
        }
        if (customUnit === 'monthly') {
          finalBymonthday = bymonthday ?? undefined;
        }
        break;
    }

    // At this point, selectedAssetNames.length > 0 is guaranteed by the guard above
    onComplete({
      assetName: selectedAssetNames[0], // Primary asset (for backwards compat)
      assetNames: selectedAssetNames, // All selected assets
      jobName,
      freq,
      interval: finalInterval,
      byday: finalByday,
      bymonthday: finalBymonthday,
      startDate,
      count: isForever ? undefined : count ?? undefined,
      isForever,
      selectedAssetIds: allSelectedAssetIds,
      customUnit: finalCustomUnit,
    });
  };

  /** Reset form for creating another job */
  const handleAddAnotherJob = () => {
    setSelectedAssetKeys([]);
    setSelectedUnitsPerAsset(new Map());
    setJobName(null);
    setFreq(null);
    setInterval('1');
    setCustomUnit('weekly');
    setByday([]);
    setBymonthday(null);
    setStartDate(null);
    setCount(null);
    setIsForever(false);
    setStep('asset');
  };

  // Check if configuration is complete (either with count or isForever)
  // Also validate that the necessary sub-fields are set for certain frequency types
  const isFreqConfigComplete = (): boolean => {
    if (!freq) return false;

    switch (freq) {
      case 'weekly':
      case 'fortnightly':
        return byday.length > 0;
      case 'monthly':
        return bymonthday !== null;
      case 'custom':
        if (customUnit === 'weekly') return byday.length > 0;
        if (customUnit === 'monthly') return bymonthday !== null;
        return true;
      default:
        return true;
    }
  };

  const isComplete =
    step === 'done' &&
    selectedAssetKeys.length > 0 &&
    jobName &&
    freq &&
    startDate &&
    (isForever || count !== null) &&
    isFreqConfigComplete();

  // Check if there are more assets that could have jobs created
  const hasMoreAssetsAvailable =
    hasMultipleAssets && remainingAssets.length > 1; // > 1 because current selected is also "remaining" until completed

  // Get the selected asset infos for summary display (keep as AssetWithKey for key-based lookups)
  const selectedAssetInfos: AssetWithKey[] = useMemo(
    () =>
      selectedAssetKeys
        .map((key) => assetKeyToAsset.get(key))
        .filter((asset): asset is AssetWithKey => !!asset),
    [selectedAssetKeys, assetKeyToAsset]
  );

  // Get asset names for job suggestions
  const selectedAssetNames = useMemo(
    () => selectedAssetInfos.map((a) => a.name),
    [selectedAssetInfos]
  );

  // Get asset types for job suggestions
  const selectedAssetTypes = selectedAssetInfos.map((a) => a.type);

  return (
    <div className='w-full space-y-6'>
      {/* Asset Selection (Multi-select with expandable units) */}
      {step === 'asset' && (
        <>
          <AssetSelection
            assets={assetsWithKeys}
            selectedAssets={selectedAssetKeys}
            completedAssets={completedAssetKeys}
            onSelectionChange={setSelectedAssetKeys}
            selectedUnitsPerAsset={selectedUnitsPerAsset}
            onUnitsChange={handleUnitsChange}
          />
          <button
            type='button'
            onClick={handleAssetSelectionConfirm}
            disabled={selectedAssetKeys.length === 0}
            className='rounded-full bg-brand px-6 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50'
          >
            Continue with {selectedAssetKeys.length}{' '}
            {selectedAssetKeys.length === 1 ? 'asset' : 'assets'}
          </button>
        </>
      )}

      {/* Job Selection */}
      {step === 'job' && selectedAssetNames.length > 0 && (
        <JobSelection
          assetNames={selectedAssetNames}
          assetTypes={selectedAssetTypes}
          industry={_industry}
          selectedJob={jobName}
          onSelect={handleJobSelect}
          onCustom={handleJobSelect}
          onInputFocus={onInputFocus}
        />
      )}

      {/* Frequency Selection */}
      {step === 'frequency' && (
        <FrequencySelection selectedFreq={freq} onSelect={handleFreqSelect} />
      )}

      {/* Days Selection (for weekly/fortnightly) */}
      {step === 'days' && (
        <>
          <DaysSelection selectedDays={byday} onDaysChange={setByday} />
          <button
            type='button'
            onClick={handleDaysComplete}
            disabled={byday.length === 0}
            className='rounded-full bg-brand px-6 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50'
          >
            Continue
          </button>
        </>
      )}

      {/* Monthly Day Selection */}
      {step === 'monthday' && (
        <MonthDayCalendarSelection
          selectedDay={bymonthday}
          onDaySelect={setBymonthday}
          onComplete={handleMonthdayComplete}
        />
      )}

      {/* Custom Frequency Selection */}
      {step === 'custom' && (
        <>
          <CustomFrequencySelection
            interval={interval}
            unit={customUnit}
            selectedDays={byday}
            bymonthday={bymonthday}
            onIntervalChange={setInterval}
            onUnitChange={(unit) => {
              setCustomUnit(unit);
              // Reset sub-selections when unit changes
              if (unit !== 'weekly') setByday([]);
              if (unit !== 'monthly') setBymonthday(null);
            }}
            onDaysChange={setByday}
            onBymonthdayChange={setBymonthday}
            onInputFocus={onInputFocus}
          />
          <button
            type='button'
            onClick={handleCustomFrequencyComplete}
            disabled={
              (customUnit === 'weekly' && byday.length === 0) ||
              (customUnit === 'monthly' && !bymonthday)
            }
            className='rounded-full bg-brand px-6 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50'
          >
            Continue
          </button>
        </>
      )}

      {/* Start Date Selection */}
      {step === 'start' && (
        <StartDateSelection
          selectedDate={startDate}
          onDateSelect={handleStartDateSelect}
          freq={freq}
          byday={byday}
          bymonthday={bymonthday}
          customUnit={customUnit}
        />
      )}

      {/* Repeat Count Selection */}
      {step === 'count' && freq && (
        <RepeatCountSelection
          selectedCount={count}
          isForever={isForever}
          onCountSelect={handleCountSelect}
          onInputFocus={onInputFocus}
        />
      )}

      {/* Summary and Complete */}
      {isComplete &&
        selectedAssetKeys.length > 0 &&
        jobName &&
        freq &&
        startDate && (
          <>
            <Summary
              config={{
                assetName: selectedAssetNames[0]!,
                assetNames: selectedAssetNames,
                jobName,
                freq,
                interval:
                  freq === 'fortnightly'
                    ? '2'
                    : freq === 'custom'
                    ? interval
                    : '1',
                byday:
                  freq === 'weekly' ||
                  freq === 'fortnightly' ||
                  (freq === 'custom' && customUnit === 'weekly')
                    ? byday
                    : undefined,
                bymonthday:
                  freq === 'monthly' ||
                  (freq === 'custom' && customUnit === 'monthly')
                    ? bymonthday ?? undefined
                    : undefined,
                startDate,
                count: isForever ? undefined : count ?? undefined,
                isForever,
                customUnit: freq === 'custom' ? customUnit : undefined,
              }}
              assetInfos={selectedAssetInfos}
              selectedUnitsPerAsset={selectedUnitsPerAsset}
            />
            <div className='flex flex-col gap-4'>
              <div className='flex flex-wrap items-center justify-between gap-2'>
                <button
                  type='button'
                  onClick={handleGoBack}
                  className='text-sm text-grey-50 hover:text-grey-70'
                  aria-label='Go back to previous step'
                >
                  ← Back
                </button>
                <div className='flex flex-wrap gap-3'>
                  <button
                    type='button'
                    onClick={onSkip}
                    className='text-grey-60 rounded-full border-2 border-grey-20 px-6 py-2 text-sm font-medium hover:border-grey-40'
                  >
                    Skip for now
                  </button>
                  <button
                    type='button'
                    onClick={handleComplete}
                    disabled={isSubmitting}
                    className='rounded-full bg-brand px-6 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50'
                  >
                    {isSubmitting ? 'Creating...' : 'Create Recurring Job'}
                  </button>
                </div>
              </div>

              {/* Option to add another job for different asset */}
              {hasMoreAssetsAvailable && (
                <div className='border-t border-grey-10 pt-4'>
                  <button
                    type='button'
                    onClick={handleAddAnotherJob}
                    className='text-sm text-brand underline hover:text-brand-dark'
                  >
                    + Add a recurring job for another asset
                  </button>
                </div>
              )}
            </div>
          </>
        )}

      {/* Post-Creation State - shows after a job was successfully created */}
      {step === 'created' && (
        <div className='w-full space-y-4'>
          <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
            <div className='flex items-center gap-2 text-green-700'>
              <FontAwesomeIcon icon={faCheck} className='h-4 w-4' />
              <span className='font-medium'>
                Recurring job created for {lastCreatedAsset}!
              </span>
            </div>
          </div>

          {remainingAssets.length > 0 ? (
            // More assets available - offer to create another job or finish
            <div className='space-y-3'>
              <div className='text-grey-60 text-sm'>
                You have {remainingAssets.length} more asset
                {remainingAssets.length !== 1 ? 's' : ''} without recurring
                jobs:
                <span className='ml-1 font-medium text-grey-70'>
                  {remainingAssets.join(', ')}
                </span>
              </div>

              <div className='flex flex-wrap gap-3'>
                <button
                  type='button'
                  onClick={handleAddAnotherJob}
                  className='rounded-full border-2 border-brand bg-white px-6 py-2.5 text-sm font-medium text-brand transition-colors hover:bg-brand hover:text-white'
                >
                  Create job for another asset
                </button>
                <button
                  type='button'
                  onClick={onFinish || onSkip}
                  className='rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-dark'
                >
                  Finish setup
                </button>
              </div>
            </div>
          ) : (
            // All assets have jobs - just show finish button
            <div className='space-y-3'>
              <div className='text-grey-60 text-sm'>
                All your assets have recurring jobs configured.
              </div>
              <button
                type='button'
                onClick={onFinish || onSkip}
                className='rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-dark'
              >
                Finish setup
              </button>
            </div>
          )}
        </div>
      )}

      {/* Back and Skip options (shown during steps, not at the end) */}
      {step !== 'done' && step !== 'created' && (
        <div className='flex items-center gap-4 pt-4'>
          {/* Back button - uses handleGoBack for proper step-by-step navigation */}
          {(getPreviousStep(step) !== null || onBack) && (
            <button
              type='button'
              onClick={handleGoBack}
              className='text-sm text-grey-50 hover:text-grey-70'
              aria-label='Go back to previous step'
            >
              ← Back
            </button>
          )}
          <button
            type='button'
            onClick={onSkip}
            className='text-sm text-grey-50 underline hover:text-grey-70'
          >
            Skip recurring job setup
          </button>
        </div>
      )}
    </div>
  );
}
